import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Tooltip } from 'antd';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useAuth } from '@hooks/useAuth';
import { useOrganization } from '@components/OrganizationBoundary';
import { ReactComponent as ListItemsSvg } from '@assets/icons/list-items.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import type { StepItemKey } from '@components/Modals/ApprovalModal/ApprovalModal/components/Steps/Steps';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import LimitReachedModal from '@components/Modals/ApprovalModal/components/LimitReachedModal';
import { getCreateAWInitialStage } from '@components/Modals/ApprovalModal/ApprovalModal/utils';
import type {
  AwState,
  AwStateStage
} from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';

import StageCard from './components/StageCard';

import './StagesStep.less';

type StagesStepProps = {
  awState: AwState;
  campaignId: string;
  ownerEmail: string;
  setAwState: (value: AwState) => void;
  onChangeStep: (value: StepItemKey) => void;
  onCloseModal: () => void;
};

export default function StagesStep({
  awState,
  setAwState,
  campaignId,
  ownerEmail,
  onChangeStep,
  onCloseModal
}: StagesStepProps) {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization(false);
  const { user } = useAuth(true);
  const [isOpenLimitReachedModal, setIsOpenLimitReachedModal] = useState(false);
  const { stages } = awState;
  const stagesWrapperRef = useRef<HTMLDivElement>(null);

  const maxStagesReached =
    stages.length >=
    (currentOrganization?.entity?.limitsV2.awStages ??
      Number.POSITIVE_INFINITY);

  const isDisabledBtn = useMemo(() => {
    if (!stages.length) return true;
    return stages.some((i) => !i.members?.length);
  }, [stages]);

  const goNextStep = () => {
    onChangeStep('settings');
  };

  const addNewStage = useCallback(() => {
    if (!awState) return;
    if (maxStagesReached) {
      setIsOpenLimitReachedModal(true);
      return;
    }
    const newStage: AwStateStage = getCreateAWInitialStage({
      email: user.email,
      name: user?.name,
      pictureUrl: user?.pictureUrl,
      me: true
    });
    setAwState({
      ...awState,
      stages: [...stages, newStage]
    });
  }, [
    awState,
    user,
    stages,
    maxStagesReached,
    setIsOpenLimitReachedModal,
    setAwState
  ]);

  const onUpdateStage = useCallback(
    (stage: AwStateStage) => {
      if (!awState) return;
      setAwState({
        ...awState,
        stages: stages.map((item) => {
          if (item.id === stage.id) return stage;
          return item;
        })
      });
    },
    [awState, stages, setAwState]
  );

  const onRemoveStage = useCallback(
    (stage: AwStateStage) => {
      if (!awState) return;
      setAwState({
        ...awState,
        stages: stages.filter((item) => item.id !== stage.id)
      });
    },
    [awState, stages, setAwState]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const stagesWrapper = stagesWrapperRef.current;
      if (!stagesWrapper) return;
      stagesWrapper.scrollLeft =
        stagesWrapper.scrollWidth + stagesWrapper.clientWidth;
    }, 100);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [stages.length]);

  return (
    <div className="approval-step stages-step">
      <div className="approval-step_head">
        <h2 className="approval-step_title">
          Stages
          <div
            className={classNames('stages-step_count', {
              'b-empty': !stages.length
            })}
          >
            {stages.length}
          </div>
          <Tooltip
            title={`${awState.name} approval routing`}
            placement="topLeft"
            overlayClassName="approval-modal-tooltip"
            className="approval-step_route-name"
          >
            {awState.name} approval routing
          </Tooltip>
        </h2>
        <div className="approval-step_text">
          Create approval stages for each group reviewing the media. Define
          steps, set due dates and assign reviewers.
        </div>
      </div>
      <div
        ref={stagesWrapperRef}
        className="approval-step_wrapper tour-route-for-approval-stages"
      >
        <div className="stages-step_wrapper">
          {!stages.length && (
            <div className="stages-step_empty">
              <ListItemsSvg />
              <Button
                size="small"
                icon={<PlusSvg />}
                className="stages-step_empty-btn"
                style={{ padding: '7px 13px', gap: 4 }}
                onClick={addNewStage}
              >
                Add first stage
              </Button>
            </div>
          )}
          {!!stages.length && (
            <>
              {stages.map((item, index) => (
                <div key={item.id} className="stages-step_block">
                  <StageCard
                    stage={item}
                    ownerEmail={ownerEmail || ''}
                    stageNumber={index + 1}
                    stages={stages}
                    campaignId={campaignId}
                    onUpdateStage={onUpdateStage}
                    onRemoveStage={onRemoveStage}
                  />
                </div>
              ))}
              <UpgradePlanTooltip
                text={
                  currentOrganization?.entity?.planType === 'team'
                    ? 'to an Enterprise plan to add more approval routing stages.'
                    : 'to a Team plan to add multiple approval routing stages.'
                }
                isCanVisible={maxStagesReached}
                beforeUpgradeClick={() => dispatch(toggleApprovalModal(null))}
                placement="topRight"
              >
                <div className="stages-step_add-stage" onClick={addNewStage}>
                  <PlusSvg className="stages-step_add-stage-icon" />
                  <div className="stages-step_add-stage-text">
                    Add stage
                    {maxStagesReached && <UpgradePlanSvg />}
                  </div>
                </div>
              </UpgradePlanTooltip>
            </>
          )}
        </div>
      </div>
      <LimitReachedModal
        isOpen={isOpenLimitReachedModal}
        title={
          currentOrganization?.entity?.planType === 'team'
            ? 'Upgrade to an Enterprise plan to add more approval routing stages.'
            : 'Upgrade to a Team plan to add multiple approval routing stages.'
        }
        text="To add more stages, please upgrade your account."
        onCancel={() => setIsOpenLimitReachedModal(false)}
      />
      <div className="approval-step_buttons">
        <Button type="text" onClick={onCloseModal}>
          Cancel
        </Button>

        <Button
          type="primary"
          disabled={isDisabledBtn}
          onClick={goNextStep}
          style={{
            minWidth: 152
          }}
        >
          Next step
        </Button>
      </div>
    </div>
  );
}
